/* Ilene Joy - Saved designs (v1, 11 Oct 2026; Ilene: "the save should be very visible").
 *
 * ONE source for the saved-designs list and its sheet, used by:
 *   - the charm builder bundle (imported; the desktop bar + phone strip "Save design" buttons call it), and
 *   - the theme (published unchanged as assets/ij-saved-designs-v1.js): the cart page's "Save design" on builder
 *     lines (templates/cart.liquid) and the cart drawer (assets/ij-ux.js loads it on demand).
 *
 * Storage: localStorage `ij_compositions_v1` (the key the old bottom-left "Save" pill used, so anything saved with it
 * still shows). One entry = { id, savedAt, title, url, thumb?, spec?, kind?, source? }, newest first, at most 24.
 * `url` is the design itself: a same-origin /products/... link in the share-link encoding (?k=2*key*value...), which
 * the product page expands back into the full design (chain, size, metal, length, Magic Middle, lariat, clasp and
 * every charm with its size, colour and karat). Nothing leaves the browser: there is no account-side storage yet
 * (no app proxy on this store), so the sheet says "kept on this device" and offers Copy link / Email me.
 *
 * Safety: everything read back from storage is rendered with textContent / attribute setters (never innerHTML);
 * links must be same-origin /products/ paths, images data:image/(jpeg|png|webp) or https.
 */
(function () {
  'use strict';
  var W = typeof window !== 'undefined' ? window : null;
  if (!W || !W.document) return;
  if (W.IJSaved && W.IJSaved.__v >= 1) return;

  var KEY = 'ij_compositions_v1';
  var MAX = 24;
  var SHARE_KEYS = ['c', 'cv', 'mm', 'mm2', 'l', 'av', 'cl', 'pk', 's', 'view'];
  var KEY_PARTS = ['c', 'cv', 'mm', 'mm2', 'l', 'cl', 's'];
  var DROP = { k: 1, debug: 1, _v: 1 };
  var URL_RE = /^\/products\/[a-z0-9][a-z0-9-]*(\?[^\s"'<>\\]{0,8000})?$/i;
  var listeners = [];
  var D = W.document;

  /* ---------------- storage ---------------- */
  function safeUrl(u) { return typeof u === 'string' && URL_RE.test(u) ? u : ''; }
  function safeThumb(u) {
    if (typeof u !== 'string' || u.length > 400000) return '';
    if (/^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/=]+$/.test(u)) return u;
    if (/^https:\/\/[^\s"'<>\\]+$/.test(u)) return u;
    if (/^\/\/[^\s"'<>\\]+$/.test(u)) return 'https:' + u;
    return '';
  }
  function read() {
    var arr;
    try { arr = JSON.parse(W.localStorage.getItem(KEY) || '[]'); } catch (e) { arr = []; }
    if (!Array.isArray(arr)) return [];
    return arr.filter(function (x) { return x && typeof x === 'object' && typeof x.id === 'string' && safeUrl(x.url); });
  }
  function write(arr) {
    arr = arr.slice(0, MAX);
    for (var tries = 0; tries < MAX + 2; tries++) {
      try { W.localStorage.setItem(KEY, JSON.stringify(arr)); return true; } catch (e) {
        /* quota: drop the image of the oldest entry that still carries an inline one, then the oldest entry */
        var dropped = false;
        for (var i = arr.length - 1; i >= 0; i--) {
          if (arr[i].thumb && arr[i].thumb.indexOf('data:') === 0) { arr[i] = Object.assign({}, arr[i], { thumb: '' }); dropped = true; break; }
        }
        if (!dropped) { if (arr.length <= 1) return false; arr = arr.slice(0, arr.length - 1); }
      }
    }
    return false;
  }
  function emit() {
    var list = read();
    listeners.slice().forEach(function (cb) { try { cb(list); } catch (e) { /* listener errors stay local */ } });
    renderSheet();
  }
  W.addEventListener('storage', function (e) { if (!e || e.key === KEY || e.key === null) emit(); });

  /* ---------------- the design link ---------------- */
  /* Expand any product link (long form, ?k=2*.. or the legacy ?k=v.X.s.Y.w.Z) into its design params. */
  function parse(url) {
    var u;
    try { u = new URL(url, W.location.origin); } catch (e) { return null; }
    var p = new URLSearchParams(u.search), out = {}, extra = [];
    var k = p.get('k');
    if (k) {
      if (k.indexOf('2*') === 0) {
        var kv = k.split('*');
        for (var j = 1; j < kv.length - 1; j += 2) if (SHARE_KEYS.indexOf(kv[j]) >= 0 && kv[j + 1]) out[kv[j]] = kv[j + 1];
      } else {
        var parts = k.split('.');
        for (var i = 0; i < parts.length - 1; i += 2) {
          if (parts[i] === 'v') out.cv = parts[i + 1];
          else if (parts[i] === 's') out.s = parts[i + 1].replace(/_/g, ',');
          else if (parts[i] === 'w') out.view = parts[i + 1];
        }
      }
    }
    p.forEach(function (v, key) {
      if (DROP[key]) return;
      if (SHARE_KEYS.indexOf(key) >= 0) { if (v !== '') out[key] = v; return; }
      extra.push([key, v]);
    });
    return { path: u.pathname, params: out, extra: extra };
  }
  /* Same encoding as the builder's share links (components/ShareModal.tsx buildCompactShareUrl), but same-origin relative. */
  function compact(url) {
    var d = parse(url);
    if (!d || !/^\/products\//.test(d.path)) return '';
    var parts = ['2'];
    for (var i = 0; i < SHARE_KEYS.length; i++) {
      var v = d.params[SHARE_KEYS[i]];
      if (v == null || v === '') continue;
      if (v.indexOf('*') >= 0) return safeUrl(d.path + new URL(url, W.location.origin).search);
      parts.push(SHARE_KEYS[i], v);
    }
    var q = 'k=' + encodeURIComponent(parts.join('*'));
    var rest = new URLSearchParams();
    d.extra.forEach(function (x) { rest.append(x[0], x[1]); });
    var r = rest.toString();
    return safeUrl(d.path + '?' + q + (r ? '&' + r : ''));
  }
  function designKey(url) {
    var d = parse(url);
    if (!d) return '';
    var s = (d.params.s || '').replace(/(,-)+$/, '');
    return d.path + '|' + KEY_PARTS.map(function (k) { return k === 's' ? s : (d.params[k] || ''); }).join('|');
  }
  function absolute(url) { return W.location.origin + url; }

  /* ---------------- names / dates ---------------- */
  function kindOf(url, kind) {
    if (kind === 'bracelet' || kind === 'necklace') return kind;
    return /bracelet/i.test(url || '') ? 'bracelet' : 'necklace';
  }
  function shortDate(ts) {
    try { return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch (e) { return ''; }
  }
  function longDate(ts) {
    try {
      var d = new Date(ts);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch (e) { return ''; }
  }
  function defaultName(kind, list) {
    var base = (kind === 'bracelet' ? 'Bracelet' : 'Necklace') + ' · ' + shortDate(Date.now());
    var taken = {};
    list.forEach(function (x) { taken[x.title] = 1; });
    if (!taken[base]) return base;
    for (var n = 2; n < 100; n++) if (!taken[base + ' · ' + n]) return base + ' · ' + n;
    return base;
  }
  function cleanName(s) { return String(s || '').replace(/[\u0000-\u001f<>]/g, '').replace(/\s+/g, ' ').trim().slice(0, 60); }
  function makeId() { return 'ij-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }

  /* ---------------- API ---------------- */
  function find(url) {
    var key = designKey(url);
    if (!key) return null;
    var list = read();
    for (var i = 0; i < list.length; i++) if (designKey(list[i].url) === key) return list[i];
    return null;
  }
  /* Save (or re-save: the same design is moved to the top, its name kept). Returns { entry, existed } or null. */
  function save(opts) {
    opts = opts || {};
    var url = compact(opts.url || (W.location.pathname + W.location.search));
    if (!url) return null;
    var list = read(), key = designKey(url), existing = null, idx = -1;
    for (var i = 0; i < list.length; i++) if (designKey(list[i].url) === key) { existing = list[i]; idx = i; break; }
    var kind = kindOf(url, opts.kind);
    var entry;
    if (existing) {
      list.splice(idx, 1);
      entry = Object.assign({}, existing, { url: url, savedAt: Date.now() });
      var t0 = safeThumb(opts.thumb);
      if (t0 && (!existing.thumb || existing.thumb.indexOf('data:') !== 0 || t0.indexOf('data:') === 0)) entry.thumb = t0;
      if (opts.spec) entry.spec = String(opts.spec).slice(0, 160);
    } else {
      entry = { id: makeId(), savedAt: Date.now(), title: cleanName(opts.title) || defaultName(kind, list), url: url, kind: kind, source: opts.source === 'cart' ? 'cart' : 'builder' };
      var t = safeThumb(opts.thumb); if (t) entry.thumb = t;
      if (opts.spec) entry.spec = String(opts.spec).slice(0, 160);
    }
    list.unshift(entry);
    var ok = write(list);
    emit();
    return ok ? { entry: entry, existed: !!existing } : null;
  }
  function update(id, patch) {
    var list = read(), hit = null;
    list = list.map(function (x) { if (x.id !== id) return x; hit = Object.assign({}, x, patch); return hit; });
    if (!hit) return null;
    write(list); emit();
    return hit;
  }
  function rename(id, name) { var n = cleanName(name); return n ? update(id, { title: n }) : null; }
  function setThumb(id, thumb) { var t = safeThumb(thumb); return t ? update(id, { thumb: t }) : null; }
  function remove(id) {
    var list = read(), at = -1, gone = null;
    for (var i = 0; i < list.length; i++) if (list[i].id === id) { at = i; gone = list[i]; break; }
    if (!gone) return null;
    list.splice(at, 1); write(list); emit();
    return { entry: gone, index: at };
  }
  function restore(entry, index) {
    var list = read().filter(function (x) { return x.id !== entry.id; });
    list.splice(Math.max(0, Math.min(index, list.length)), 0, entry);
    write(list); emit();
  }
  function onChange(cb) {
    listeners.push(cb);
    return function () { listeners = listeners.filter(function (x) { return x !== cb; }); };
  }
  function emailHref(entry, to) {
    var link = absolute(entry.url);
    var addr = String(to || '').replace(/[^A-Za-z0-9.!#$%&'*+\/=?^_`{|}~@-]/g, '');
    var body = 'My Ilene Joy design' + (entry.title ? ' "' + entry.title + '"' : '') + ':\n' + link + '\n\nOpen the link to continue exactly where I left off.';
    return 'mailto:' + addr + '?subject=' + encodeURIComponent('My Ilene Joy design' + (entry.title ? ' · ' + entry.title : '')) + '&body=' + encodeURIComponent(body);
  }
  function copyLink(entry) {
    var link = absolute(entry.url);
    return new Promise(function (resolve) {
      try {
        var p = W.navigator.clipboard && W.navigator.clipboard.writeText(link);
        if (p && typeof p.then === 'function') p.then(function () { resolve(true); }, function () { resolve(false); });
        else resolve(false);
      } catch (e) { resolve(false); }
    });
  }

  /* ---------------- the sheet ---------------- */
  var CSS = [
    'html body .ijsd{position:fixed;inset:0;z-index:10080;display:flex;align-items:center;justify-content:center;padding:24px;box-sizing:border-box;font-family:var(--s-ui,var(--a-ui,NHG_55,"Helvetica Neue",Helvetica,Arial,sans-serif));color:#151515;-webkit-font-smoothing:antialiased}',
    'html body .ijsd[hidden]{display:none!important}',
    'html body .ijsd *,html body .ijsd *::before,html body .ijsd *::after{box-sizing:border-box}',
    'html body .ijsd__scrim{position:absolute;inset:0;background:rgba(21,21,21,.32);opacity:0;transition:opacity .22s ease}',
    'html body .ijsd.is-in .ijsd__scrim{opacity:1}',
    'html body .ijsd__card{position:relative;width:100%;max-width:480px;max-height:min(760px,calc(100vh - 48px));overflow:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;background:#fff;border:1px solid #e5e5e5;box-shadow:0 24px 60px rgba(0,0,0,.14);padding:34px 32px 28px;outline:none;opacity:0;transform:translateY(10px);transition:opacity .22s ease,transform .26s cubic-bezier(.2,.7,.2,1)}',
    'html body .ijsd.is-in .ijsd__card{opacity:1;transform:none}',
    'html body .ijsd .ijsd__close{position:absolute;top:14px;right:14px;width:40px;height:40px;min-height:40px;display:grid;place-items:center;margin:0;padding:0;background:#fff;border:0;border-radius:0;color:#151515;cursor:pointer;box-shadow:none}',
    'html body .ijsd .ijsd__close:hover{background:#f6f6f6}',
    'html body .ijsd p,html body .ijsd span,html body .ijsd div,html body .ijsd li,html body .ijsd a,html body .ijsd button,html body .ijsd input,html body .ijsd label,html body .ijsd h2,html body .ijsd h3{font-size:13px;line-height:1.45;letter-spacing:0;text-transform:none;font-style:normal}',
    'html body .ijsd .ijsd__eyebrow{margin:0 0 12px;font-size:10.5px;line-height:1;letter-spacing:.2em;text-transform:uppercase;color:#6d6d6d;font-weight:400}',
    'html body .ijsd .ijsd__title{margin:0 0 22px;padding-right:36px;font-family:var(--s-display,var(--a-display,"Fraunces",Georgia,serif));font-weight:300;font-size:30px;line-height:1.08;letter-spacing:-.01em;color:#151515}',
    'html body .ijsd .ijsd__hero{display:grid;grid-template-columns:104px minmax(0,1fr);gap:18px;align-items:center}',
    'html body .ijsd .ijsd__thumb{display:block;width:100%;aspect-ratio:1/1;background:#f6f6f6;border:1px solid #e5e5e5;overflow:hidden;position:relative}',
    'html body .ijsd .ijsd__thumb img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;display:block;max-width:none;margin:0;padding:0}',
    'html body .ijsd .ijsd__label{display:block;margin:0 0 6px;font-size:10.5px;line-height:1;letter-spacing:.18em;text-transform:uppercase;color:#6d6d6d}',
    'html body .ijsd .ijsd__name{display:block;width:100%;height:44px;min-height:44px;margin:0;padding:0 12px;border:1px solid #cfcfcf;border-radius:0;background:#fff;color:#151515;font-family:var(--s-display,var(--a-display,"Fraunces",Georgia,serif));font-weight:300;font-size:19px;line-height:1.2;box-shadow:none;-webkit-appearance:none;appearance:none;outline:none;transition:border-color .16s ease}',
    'html body .ijsd .ijsd__name:focus{border-color:#151515}',
    'html body .ijsd .ijsd__meta{margin:8px 0 0;font-size:12px;color:#6d6d6d;font-variant-numeric:lining-nums}',
    'html body .ijsd .ijsd__note{margin:18px 0 0;padding:12px 0 0;border-top:1px solid #e5e5e5;font-size:12.5px;line-height:1.5;color:#6d6d6d}',
    'html body .ijsd .ijsd__btn{display:inline-flex;align-items:center;justify-content:center;gap:9px;height:46px;min-height:46px;margin:0;padding:0 14px;border:1px solid #151515;border-radius:0;background:#fff;color:#151515;font-size:13.5px;font-weight:500;letter-spacing:.01em;text-decoration:none;white-space:nowrap;cursor:pointer;box-shadow:none;transition:background-color .16s ease,color .16s ease}',
    'html body .ijsd .ijsd__btn:hover{background:#151515;color:#fff}',
    'html body .ijsd .ijsd__btn svg{flex:0 0 auto}',
    'html body .ijsd form.ijsd__email{display:block!important;max-width:none!important;width:auto!important;margin:18px 0 0!important;padding:0!important;text-align:left!important;border:0!important;background:none!important}',
    'html body .ijsd .ijsd__email-row{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px;margin:0}',
    'html body .ijsd .ijsd__email-input{display:block;width:100%;height:46px;min-height:46px;margin:0;padding:0 12px;border:1px solid #cfcfcf;border-radius:0;background:#fff;color:#151515;font-family:var(--s-ui,var(--a-ui,NHG_55,"Helvetica Neue",sans-serif));font-size:15px;line-height:1.2;letter-spacing:0;text-transform:none;text-align:left;box-shadow:none;-webkit-appearance:none;appearance:none;outline:none}',
    'html body .ijsd .ijsd__email-input:focus{border-color:#151515}',
    'html body .ijsd .ijsd__email-send,html body .ijsd .ijsd__btn--ink{background:#151515;color:#fff;border-color:#151515}',
    'html body .ijsd .ijsd__email-send:hover,html body .ijsd .ijsd__btn--ink:hover{background:#000;color:#fff}',
    'html body .ijsd .ijsd__consent{display:flex;align-items:flex-start;gap:10px;margin:12px 0 0;padding:0;font-size:12px;line-height:1.45;color:#6d6d6d;text-align:left;text-transform:none;letter-spacing:0;cursor:pointer}',
    'html body .ijsd .ijsd__consent span{font-size:12px;line-height:1.45;color:#6d6d6d}',
    'html body .ijsd .ijsd__consent input{flex:0 0 auto;width:16px;height:16px;min-height:0;margin:1px 0 0;padding:0;accent-color:#151515;-webkit-appearance:auto;appearance:auto}',
    'html body .ijsd .ijsd__email-msg{margin:10px 0 0;font-size:12.5px;line-height:1.45;color:#151515}',
    'html body .ijsd .ijsd__flash{margin:18px 0 0;padding:16px;border:1px solid #e5e5e5;background:#f6f6f6}',
    'html body .ijsd .ijsd__flash-title{margin:0 0 4px;font-family:var(--s-display,var(--a-display,"Fraunces",Georgia,serif));font-weight:300;font-size:19px;line-height:1.2;color:#151515}',
    'html body .ijsd .ijsd__flash-text{margin:0 0 12px;font-size:12.5px;color:#6d6d6d}',
    'html body .ijsd .ijsd__actions{display:flex;gap:16px;margin:14px 0 0}',
    'html body .ijsd .ijsd__actions .ijsd__act{gap:7px}',
    'html body .ijsd .ijsd__status{min-height:18px;margin:10px 0 0;font-size:12px;color:#151515}',
    'html body .ijsd .ijsd__copyfield{display:block;width:100%;height:40px;margin:6px 0 0;padding:0 10px;border:1px solid #cfcfcf;border-radius:0;font-size:12px;color:#151515;background:#f6f6f6}',
    'html body .ijsd .ijsd__list-wrap{margin:26px 0 0}',
    'html body .ijsd .ijsd__current + .ijsd__list-wrap{padding-top:22px;border-top:1px solid #e5e5e5}',
    'html body .ijsd .ijsd__list-head{display:flex;align-items:baseline;justify-content:space-between;margin:0 0 6px;font-size:10.5px;line-height:1;letter-spacing:.2em;text-transform:uppercase;color:#6d6d6d;font-weight:400}',
    'html body .ijsd .ijsd__list-head span{font-size:inherit;line-height:inherit;letter-spacing:inherit;text-transform:inherit;color:inherit}',
    'html body .ijsd .ijsd__status:empty{display:none}',
    'html body .ijsd .ijsd__list{list-style:none;margin:0;padding:0}',
    'html body .ijsd .ijsd__item{display:grid;grid-template-columns:64px minmax(0,1fr);gap:14px;align-items:center;margin:0;padding:12px 0;border-bottom:1px solid #e5e5e5;list-style:none}',
    'html body .ijsd .ijsd__item:last-child{border-bottom:0}',
    'html body .ijsd .ijsd__item.is-current .ijsd__item-name::after{content:"This design";margin-left:8px;padding:2px 6px;border:1px solid #cfcfcf;font-family:var(--s-ui,NHG_55,"Helvetica Neue",sans-serif);font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;color:#6d6d6d;vertical-align:2px;white-space:nowrap}',
    'html body .ijsd .ijsd__item-main{min-width:0}',
    'html body .ijsd a.ijsd__item-name{display:block;margin:0;color:#151515;text-decoration:none;font-family:var(--s-display,var(--a-display,"Fraunces",Georgia,serif));font-weight:300;font-size:17px;line-height:1.25;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
    'html body .ijsd a.ijsd__item-name:hover{text-decoration:underline;text-underline-offset:4px;text-decoration-thickness:1px}',
    'html body .ijsd .ijsd__item-meta{margin:3px 0 0;font-size:11.5px;color:#6d6d6d;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-variant-numeric:lining-nums}',
    'html body .ijsd .ijsd__item-actions{display:flex;flex-wrap:wrap;gap:4px 16px;margin:6px 0 0}',
    'html body .ijsd .ijsd__act{display:inline-flex;align-items:center;min-height:32px;margin:0;padding:0;border:0;border-radius:0;background:none;box-shadow:none;color:#151515;font-size:12px;font-weight:500;letter-spacing:.01em;text-decoration:underline;text-underline-offset:4px;text-decoration-thickness:1px;text-decoration-color:#cfcfcf;cursor:pointer}',
    'html body .ijsd .ijsd__act:hover{text-decoration-color:#151515}',
    'html body .ijsd .ijsd__act--muted{color:#6d6d6d}',
    'html body .ijsd .ijsd__item .ijsd__thumb{width:64px}',
    'html body .ijsd .ijsd__rename{display:flex;gap:8px;align-items:center}',
    'html body .ijsd .ijsd__rename input{flex:1 1 auto;min-width:0;height:38px;min-height:38px;margin:0;padding:0 10px;border:1px solid #151515;border-radius:0;font-family:var(--s-display,var(--a-display,"Fraunces",Georgia,serif));font-weight:300;font-size:16px;color:#151515;background:#fff;box-shadow:none;outline:none}',
    'html body .ijsd .ijsd__removed{grid-column:1/-1;display:flex;justify-content:space-between;align-items:center;font-size:12.5px;color:#6d6d6d}',
    'html body .ijsd .ijsd__empty{margin:6px 0 0;font-size:13px;color:#6d6d6d}',
    'html body .ijsd :is(button,a,input):focus-visible{outline:1px solid #151515;outline-offset:3px}',
    'html body.ijsd-lock{overflow:hidden!important}',
    '@media (max-width:640px){',
    '  html body .ijsd{align-items:flex-end;padding:0}',
    '  html body .ijsd__card{max-width:none;max-height:min(88vh,calc(100% - 24px));padding:28px 20px calc(22px + env(safe-area-inset-bottom,0px));border:0;border-top:1px solid #e5e5e5;box-shadow:0 -12px 40px rgba(0,0,0,.12);transform:translateY(24px)}',
    '  html body .ijsd .ijsd__title{font-size:27px;margin-bottom:18px}',
    '  html body .ijsd .ijsd__hero{grid-template-columns:88px minmax(0,1fr);gap:14px}',
    '  html body .ijsd .ijsd__btn{padding:0 10px;font-size:13px}',
    '  html body .ijsd .ijsd__email-row{grid-template-columns:1fr}',
    '  html body .ijsd .ijsd__email-input{font-size:16px}',
    '  html body .ijsd .ijsd__close{top:10px;right:8px}',
    '}',
    '@media (prefers-reduced-motion:reduce){html body .ijsd__card,html body .ijsd__scrim{transition:none!important}}',
    /* cart page + drawer: the line's Save design (next to Edit design) and the "Saved designs" opener */
    'html body .ij-save-chip{display:inline-flex!important;width:auto!important;max-width:none!important;flex:0 0 auto;float:none!important;align-items:center;gap:7px;height:34px;min-height:34px;margin:0;padding:0 12px!important;border:1px solid #151515!important;border-radius:0!important;background:#fff!important;color:#151515!important;font-family:var(--s-ui,var(--a-ui,NHG_55,"Helvetica Neue",Helvetica,Arial,sans-serif));font-size:12.5px;font-weight:500;font-style:normal;letter-spacing:.01em;line-height:1;text-transform:none;text-decoration:none;cursor:pointer;box-shadow:none;white-space:nowrap;vertical-align:middle;transition:background-color .16s ease,color .16s ease}',
    'html body .ij-save-chip:hover{background:#151515!important;color:#fff!important}',
    'html body .ij-save-chip span{font-size:inherit!important;line-height:inherit!important;letter-spacing:inherit!important;color:inherit!important}',
    'html body .ij-save-chip svg{flex:0 0 auto}',
    'html body .ij-save-chip.is-saved svg path{fill:currentColor}',
    'html body .ij-save-chip:focus-visible,html body .ij-saved-link:focus-visible{outline:1px solid #151515;outline-offset:3px}',
    'html body .ij-saved-link{display:inline-flex;width:auto!important;align-self:flex-start;align-items:center;gap:6px;min-height:32px;margin:0;padding:0;border:0;border-radius:0;background:none;box-shadow:none;color:#151515;font-family:var(--s-ui,var(--a-ui,NHG_55,"Helvetica Neue",Helvetica,Arial,sans-serif));font-size:12.5px;font-weight:400;letter-spacing:.01em;line-height:1;text-transform:none;cursor:pointer}',
    'html body .ij-saved-link[hidden]{display:none!important}',
    'html body .ij-saved-link > span:first-of-type{text-decoration:underline;text-underline-offset:4px;text-decoration-thickness:1px;text-decoration-color:#cfcfcf}',
    'html body .ij-saved-link:hover > span:first-of-type{text-decoration-color:#151515}',
    'html body .ij-saved-link span{font-size:inherit!important;line-height:inherit!important;color:inherit!important}',
    'html body .ij-saved-link [data-ij-saved-count]{min-width:20px;height:20px;padding:0 6px;display:inline-grid;place-items:center;border:1px solid #cfcfcf;font-size:11px!important;font-variant-numeric:tabular-nums}',
  ].join('\n');

  var rendering = false;
  var sheet = null, state = { mode: 'list', id: null, returnTo: null, rename: null, removed: null, removedTimer: 0 };
  function el(tag, cls, text) {
    var n = D.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function svg(path, size) {
    var NS = 'http://www.w3.org/2000/svg', s = D.createElementNS(NS, 'svg');
    s.setAttribute('viewBox', '0 0 16 16'); s.setAttribute('width', String(size || 15)); s.setAttribute('height', String(size || 15));
    s.setAttribute('fill', 'none'); s.setAttribute('stroke', 'currentColor'); s.setAttribute('stroke-width', '1.3');
    s.setAttribute('stroke-linecap', 'round'); s.setAttribute('stroke-linejoin', 'round'); s.setAttribute('aria-hidden', 'true');
    var p = D.createElementNS(NS, 'path'); p.setAttribute('d', path); s.appendChild(p);
    return s;
  }
  var ICON_LINK = 'M6.5 9.5l3-3M7 4.5l1.2-1.2a2.5 2.5 0 013.5 3.5L10.5 8M9 11.5l-1.2 1.2a2.5 2.5 0 01-3.5-3.5L5.5 8';
  var ICON_MAIL = 'M2 4h12v8H2zM2.5 4.5L8 9l5.5-4.5';
  function ensureCss() {
    if (D.getElementById('ijsd-css')) return;
    var st = D.createElement('style'); st.id = 'ijsd-css'; st.textContent = CSS;
    (D.head || D.documentElement).appendChild(st);
  }
  function ensureSheet() {
    ensureCss();
    if (sheet && D.body.contains(sheet.root)) return sheet;
    var root = el('div', 'ijsd'); root.hidden = true;
    root.setAttribute('role', 'dialog'); root.setAttribute('aria-modal', 'true'); root.setAttribute('aria-labelledby', 'ijsd-title');
    root.setAttribute('data-ij-saved-sheet', '');
    var scrim = el('div', 'ijsd__scrim');
    var card = el('div', 'ijsd__card'); card.tabIndex = -1;
    var close = el('button', 'ijsd__close'); close.type = 'button'; close.setAttribute('aria-label', 'Close');
    close.appendChild(svg('M4 4l8 8M12 4l-8 8', 16));
    var eyebrow = el('p', 'ijsd__eyebrow', 'Saved designs');
    var title = el('h2', 'ijsd__title'); title.id = 'ijsd-title';
    var body = el('div', 'ijsd__body');
    card.appendChild(close); card.appendChild(eyebrow); card.appendChild(title); card.appendChild(body);
    root.appendChild(scrim); root.appendChild(card);
    D.body.appendChild(root);
    scrim.addEventListener('click', closeSheet);
    close.addEventListener('click', closeSheet);
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); if (state.rename) { state.rename = null; renderSheet(); return; } closeSheet(); return; }
      if (e.key !== 'Tab') return;
      var f = Array.prototype.filter.call(card.querySelectorAll('a[href],button,input'), function (n) { return !n.disabled && n.offsetParent !== null; });
      if (!f.length) return;
      if (e.shiftKey && D.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && D.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    });
    /* the theme closes its own popovers on any document click - keep ours from bubbling into them */
    root.addEventListener('click', function (e) { e.stopPropagation(); });
    sheet = { root: root, card: card, title: title, body: body };
    return sheet;
  }
  function thumbNode(entry) {
    var box = el('span', 'ijsd__thumb');
    var src = safeThumb(entry.thumb);
    if (src) { var im = el('img'); im.alt = ''; im.decoding = 'async'; im.src = src; box.appendChild(im); }
    return box;
  }
  function renderSheet() {
    if (!sheet || sheet.root.hidden) return;
    var list = read();
    var cur = state.id ? list.filter(function (x) { return x.id === state.id; })[0] : null;
    if (state.mode === 'saved' && !cur) state.mode = 'list';
    sheet.title.textContent = state.mode === 'saved' ? 'Your design is saved.' : 'Your saved designs.';
    var body = sheet.body;
    var keepFocusName = D.activeElement && D.activeElement.classList && D.activeElement.classList.contains('ijsd__name');
    var pendingName = keepFocusName ? D.activeElement.value : null;
    var ae = D.activeElement, refocus = ae && ae.closest && ae.closest('form.ijsd__email') ? ae : null;
    rendering = true;
    homeForm();
    while (body.firstChild) body.removeChild(body.firstChild);
    rendering = false;

    if (state.mode === 'saved' && cur) {
      var sec = el('section', 'ijsd__current');
      var hero = el('div', 'ijsd__hero');
      hero.appendChild(thumbNode(cur));
      var txt = el('div', 'ijsd__hero-text');
      var lab = el('label', 'ijsd__label', 'Name'); lab.setAttribute('for', 'ijsd-name');
      var inp = el('input', 'ijsd__name'); inp.id = 'ijsd-name'; inp.type = 'text'; inp.maxLength = 60; inp.value = cur.title || ''; inp.autocomplete = 'off';
      inp.setAttribute('data-ij-saved-name', '');
      if (pendingName != null) inp.value = pendingName;
      var commit = function () { if (rendering) return; var n = cleanName(inp.value); if (n && n !== cur.title) rename(cur.id, n); else if (!n) inp.value = cur.title; };
      inp.addEventListener('change', commit);
      inp.addEventListener('blur', commit);
      inp.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); commit(); inp.blur(); } });
      txt.appendChild(lab); txt.appendChild(inp);
      txt.appendChild(el('p', 'ijsd__meta', 'Saved ' + longDate(cur.savedAt)));
      hero.appendChild(txt);
      sec.appendChild(hero);
      sec.appendChild(el('p', 'ijsd__note', 'Saved on this device, no account needed. To open it anywhere else, email it to yourself or copy the link.'));
      sec.appendChild(emailNode(cur));
      var acts = el('div', 'ijsd__actions');
      var copy = el('button', 'ijsd__act'); copy.type = 'button'; copy.setAttribute('data-ij-saved-copy', '');
      copy.appendChild(svg(ICON_LINK, 14)); copy.appendChild(el('span', '', 'Copy link'));
      acts.appendChild(copy);
      sec.appendChild(acts);
      var status = el('p', 'ijsd__status'); status.setAttribute('role', 'status');
      sec.appendChild(status);
      copy.addEventListener('click', function () {
        copyLink(cur).then(function (ok) {
          status.textContent = ok ? 'Link copied. Open it on any device to continue this exact design.' : 'Copy this link to continue this exact design on any device:';
          if (!ok) {
            var f = el('input', 'ijsd__copyfield'); f.type = 'text'; f.readOnly = true; f.value = absolute(cur.url); f.setAttribute('aria-label', 'Design link');
            f.addEventListener('focus', function () { f.select(); });
            if (status.nextSibling && status.nextSibling.classList && status.nextSibling.classList.contains('ijsd__copyfield')) sec.removeChild(status.nextSibling);
            sec.insertBefore(f, status.nextSibling); f.focus();
          }
        });
      });
      body.appendChild(sec);
      if (keepFocusName) inp.focus();
    }

    var wrap = el('section', 'ijsd__list-wrap');
    var others = list;
    var head = el('h3', 'ijsd__list-head');
    head.appendChild(el('span', '', state.mode === 'saved' ? 'All saved designs' : 'On this device'));
    head.appendChild(el('span', '', String(others.length)));
    wrap.appendChild(head);
    if (!others.length) {
      wrap.appendChild(el('p', 'ijsd__empty', 'Nothing saved yet. Use “Save design” while you design and it will wait for you here.'));
    }
    var ul = el('ul', 'ijsd__list');
    others.forEach(function (x, i) { ul.appendChild(itemNode(x, i)); });
    if (state.removed) {
      var r = state.removed, li = el('li', 'ijsd__item');
      var row = el('div', 'ijsd__removed');
      row.appendChild(el('span', '', 'Removed “' + (r.entry.title || 'Design') + '”'));
      var undo = el('button', 'ijsd__act'); undo.type = 'button'; undo.textContent = 'Undo';
      undo.addEventListener('click', function () { clearTimeout(state.removedTimer); var rr = state.removed; state.removed = null; restore(rr.entry, rr.index); });
      row.appendChild(undo); li.appendChild(row);
      ul.insertBefore(li, ul.children[Math.min(r.index, ul.children.length)] || null);
    }
    wrap.appendChild(ul);
    body.appendChild(wrap);
    if (refocus && D.body.contains(refocus)) { try { refocus.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
  }
  function itemNode(x, i) {
    var li = el('li', 'ijsd__item' + (x.id === state.id ? ' is-current' : ''));
    li.setAttribute('data-ij-saved-item', x.id);
    var thumbLink = el('a'); thumbLink.href = x.url; thumbLink.setAttribute('aria-hidden', 'true'); thumbLink.tabIndex = -1;
    thumbLink.appendChild(thumbNode(x));
    li.appendChild(thumbLink);
    var main = el('div', 'ijsd__item-main');
    if (state.rename === x.id) {
      var box = el('div', 'ijsd__rename');
      var inp = el('input'); inp.type = 'text'; inp.maxLength = 60; inp.value = x.title || ''; inp.setAttribute('aria-label', 'New name');
      var ok = el('button', 'ijsd__act', 'Save'); ok.type = 'button';
      var done = function () { var n = cleanName(inp.value); state.rename = null; if (n) rename(x.id, n); else renderSheet(); };
      ok.addEventListener('click', done);
      inp.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); done(); } });
      box.appendChild(inp); box.appendChild(ok); main.appendChild(box);
      setTimeout(function () { try { inp.focus(); inp.select(); } catch (e) { /* detached */ } }, 0);
    } else {
      var name = el('a', 'ijsd__item-name', x.title || 'Design'); name.href = x.url; name.setAttribute('data-ij-saved-open', '');
      main.appendChild(name);
    }
    main.appendChild(el('p', 'ijsd__item-meta', [shortDate(x.savedAt), x.spec || ''].filter(Boolean).join(' · ')));
    var acts = el('div', 'ijsd__item-actions');
    var open = el('a', 'ijsd__act', 'Open'); open.href = x.url;
    var ren = el('button', 'ijsd__act ijsd__act--muted', 'Rename'); ren.type = 'button'; ren.setAttribute('data-ij-saved-rename', '');
    ren.addEventListener('click', function () { state.rename = x.id; renderSheet(); });
    var rm = el('button', 'ijsd__act ijsd__act--muted', 'Remove'); rm.type = 'button'; rm.setAttribute('data-ij-saved-remove', '');
    rm.setAttribute('aria-label', 'Remove ' + (x.title || 'design'));
    rm.addEventListener('click', function () {
      var r = remove(x.id);
      if (!r) return;
      if (state.id === x.id) { state.id = null; state.mode = 'list'; }
      clearTimeout(state.removedTimer);
      state.removed = r;
      state.removedTimer = setTimeout(function () { state.removed = null; renderSheet(); }, 6000);
      renderSheet();
    });
    acts.appendChild(open); acts.appendChild(ren); acts.appendChild(rm);
    main.appendChild(acts);
    li.appendChild(main);
    return li;
  }
  /* ---------------- Email me my design ----------------
     snippets/ij-saved-email-form.liquid renders a real Shopify customer form (#ij-saved-email-form) on every page; it is moved
     into the sheet while the sheet shows a saved design. Shopify's customer form always subscribes the address to marketing,
     so it is posted ONLY when the consent box is ticked; otherwise (or without the snippet) the link goes by mailto only.
     Shopify cannot send the link itself, so after a post the sheet offers the mailto, addressed to the visitor. */
  var EMAIL_KEY = 'ij_saved_email', PENDING = 'ij_saved_email_pending', EMAIL_RE = /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/;
  var flash = null, fallback = null;
  function emailForm() { return D.getElementById('ij-saved-email-form'); }
  function homeForm() {
    var f = emailForm(), h = D.getElementById('ij-saved-email-home');
    if (f && h && f.parentNode !== h) h.appendChild(f);
  }
  function buildFallback() {
    var f = el('form', 'ijsd__email'); f.setAttribute('data-ij-fallback', ''); f.noValidate = true;
    f.appendChild(el('p', 'ijsd__label ijsd__email-label', 'Email me my design'));
    var row = el('div', 'ijsd__email-row');
    var i = el('input', 'ijsd__email-input'); i.type = 'email'; i.autocomplete = 'email'; i.placeholder = 'Your email'; i.setAttribute('aria-label', 'Your email');
    var b = el('button', 'ijsd__btn ijsd__email-send', 'Email me my design'); b.type = 'button';
    row.appendChild(i); row.appendChild(b); f.appendChild(row);
    return f;
  }
  function storedEmail() { try { return W.localStorage.getItem(EMAIL_KEY) || ''; } catch (e) { return ''; } }
  function emailMsg(f, text) {
    var m = f.querySelector('.ijsd__email-msg');
    if (!m) { m = el('p', 'ijsd__email-msg'); m.setAttribute('role', 'status'); f.appendChild(m); }
    m.textContent = text;
  }
  var allowPost = false;
  /* every submit of the email form passes here first (window, capture phase - before Shopify's own captcha handler on the form):
     only a consented send (allowPost) reaches Shopify; Enter in the field and the button both go through sendEmail */
  W.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !f.classList || !f.classList.contains('ijsd__email') || allowPost) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    sendEmail(f);
  }, true);
  D.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('.ijsd__email-send') : null;
    if (!b) return;
    var f = b.closest('form.ijsd__email');
    if (!f) return;
    e.preventDefault();
    sendEmail(f);
  }, true);
  function sendEmail(f) {
    var entry = f.__ijsdEntry;
    var input = f.querySelector('.ijsd__email-input'), email = input ? String(input.value || '').trim() : '';
    if (!entry || !EMAIL_RE.test(email)) { emailMsg(f, 'Please enter your email address.'); if (input) input.focus(); return; }
    try { W.localStorage.setItem(EMAIL_KEY, email); } catch (er) { /* private mode */ }
    var box = f.querySelector('[data-ij-saved-consent]');
    if (f.hasAttribute('data-ij-fallback') || !box || !box.checked) {
      emailMsg(f, 'Your mail app opens with your design link, addressed to you. Press send to keep it.');
      W.location.href = emailHref(entry, email);
      return;
    }
    /* consent given: Shopify posts the form (page reloads); the sheet re-opens with the result */
    try { W.sessionStorage.setItem(PENDING, JSON.stringify({ id: entry.id, email: email, at: Date.now() })); } catch (er) { /* ignore */ }
    var send = f.querySelector('.ijsd__email-send'); if (send) send.textContent = 'Sending\u2026';
    allowPost = true;
    try { if (typeof f.requestSubmit === 'function') f.requestSubmit(); else f.submit(); } finally { allowPost = false; }
  }
  function emailNode(cur) {
    var wrap = el('div', 'ijsd__email-wrap');
    if (flash && flash.id === cur.id && flash.kind === 'ok') {
      var fl = el('div', 'ijsd__flash'); fl.setAttribute('role', 'status');
      fl.appendChild(el('p', 'ijsd__flash-title', 'Thank you. You\u2019re on our list.'));
      fl.appendChild(el('p', 'ijsd__flash-text', 'We\u2019ll keep you posted at ' + flash.email + '. Send your design link to yourself so it is in your inbox:'));
      var a = el('a', 'ijsd__btn ijsd__btn--ink'); a.href = emailHref(cur, flash.email); a.setAttribute('data-ij-saved-email', '');
      a.appendChild(svg(ICON_MAIL)); a.appendChild(el('span', '', 'Send the link to me'));
      fl.appendChild(a);
      wrap.appendChild(fl);
      return wrap;
    }
    var f = emailForm();
    if (!f) { if (!fallback) fallback = buildFallback(); f = fallback; }
    f.__ijsdEntry = cur;
    var input = f.querySelector('.ijsd__email-input');
    if (input && !input.value) input.value = storedEmail();
    var old = f.querySelector('.ijsd__email-msg'); if (old) old.parentNode.removeChild(old);
    if (flash && flash.id === cur.id && flash.kind === 'err') emailMsg(f, 'We couldn\u2019t add ' + flash.email + (flash.text ? ': ' + flash.text : '.') + ' You can still send the link with your mail app (untick the box).');
    var send = f.querySelector('.ijsd__email-send'); if (send) send.textContent = 'Email me my design';
    wrap.appendChild(f);
    return wrap;
  }
  /* after Shopify's post-back: re-open the sheet on the design that was emailed, with the result */
  function resumePosted() {
    var p = null;
    try { p = JSON.parse(W.sessionStorage.getItem(PENDING) || 'null'); } catch (e) { p = null; }
    if (!p || !p.id) return;
    var mark = D.querySelector('[data-ij-saved-posted]'), v = mark ? mark.getAttribute('data-ij-saved-posted') : '';
    if (!v) { if (Date.now() - (p.at || 0) > 15 * 60 * 1000) { try { W.sessionStorage.removeItem(PENDING); } catch (e) { /* ignore */ } } return; }
    try { W.sessionStorage.removeItem(PENDING); } catch (e) { /* ignore */ }
    var txt = (mark.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 200);
    var ok = v === 'ok' || /taken|already/i.test(txt);
    if (!read().some(function (x) { return x.id === p.id; })) return;
    flash = { id: p.id, kind: ok ? 'ok' : 'err', email: String(p.email || '').slice(0, 120), text: ok ? '' : txt };
    openSheet({ mode: 'saved', id: p.id });
  }

  function openSheet(opts) {
    opts = opts || {};
    var s = ensureSheet();
    state.mode = opts.mode === 'saved' && opts.id ? 'saved' : 'list';
    state.id = opts.id || null;
    state.rename = null; state.removed = null;
    state.returnTo = opts.returnFocus || D.activeElement;
    var wasHidden = s.root.hidden;
    s.root.hidden = false;
    D.body.classList.add('ijsd-lock');
    renderSheet();
    if (wasHidden) {
      s.root.classList.remove('is-in');
      void s.root.offsetWidth;
      W.requestAnimationFrame(function () { s.root.classList.add('is-in'); });
    }
    try { s.card.focus({ preventScroll: true }); } catch (e) { s.card.focus(); }
    s.card.scrollTop = 0;
  }
  function closeSheet() {
    if (!sheet || sheet.root.hidden) return;
    var a = D.activeElement;
    if (a && a.classList && a.classList.contains('ijsd__name')) a.blur();
    sheet.root.classList.remove('is-in');
    sheet.root.hidden = true;
    homeForm();
    flash = null;
    D.body.classList.remove('ijsd-lock');
    var back = state.returnTo;
    state.returnTo = null;
    if (back && typeof back.focus === 'function' && D.body.contains(back)) { try { back.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
  }

  /* ---------------- theme buttons (cart page / drawer) ----------------
     <button data-ij-save-design data-url="/products/..?.." data-thumb="https://.." data-kind="bracelet" data-spec="..">Save design</button>
     <button data-ij-saved-list>Saved designs</button>  (opens the list) */
  function syncButtons() {
    var btns = D.querySelectorAll('[data-ij-save-design]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i], hit = find(b.getAttribute('data-url') || '');
      if (!b.hasAttribute('data-ij-sync')) b.setAttribute('data-ij-sync', '');
      if (b.classList.contains('is-saved') !== !!hit) b.classList.toggle('is-saved', !!hit);
      var lab = b.querySelector('[data-ij-save-label]'), want = hit ? 'Saved' : 'Save design';
      if (lab && lab.textContent !== want) lab.textContent = want;
      var al = hit ? 'Saved – view your saved designs' : 'Save design';
      if (b.getAttribute('aria-label') !== al) b.setAttribute('aria-label', al);
    }
    var n = read().length, cs = D.querySelectorAll('[data-ij-saved-count]'), nt = n ? String(n) : '';
    for (var j = 0; j < cs.length; j++) { if (cs[j].textContent !== nt) cs[j].textContent = nt; if (cs[j].hidden !== !n) cs[j].hidden = !n; }
    var au = D.querySelectorAll('[data-ij-saved-auto]');
    for (var k = 0; k < au.length; k++) if (au[k].hidden !== !n) au[k].hidden = !n;
  }
  D.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-ij-save-design],[data-ij-saved-list]') : null;
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();
    if (t.hasAttribute('data-ij-saved-list')) { openSheet({ mode: 'list', returnFocus: t }); return; }
    var url = t.getAttribute('data-url') || '';
    var hit = find(url);
    if (hit) { openSheet({ mode: 'saved', id: hit.id, returnFocus: t }); return; }
    var r = save({ url: url, thumb: t.getAttribute('data-thumb') || '', kind: t.getAttribute('data-kind') || '', spec: t.getAttribute('data-spec') || '', source: t.getAttribute('data-source') || 'cart' });
    if (r) openSheet({ mode: 'saved', id: r.entry.id, returnFocus: t });
  }, true);
  onChange(syncButtons);
  ensureCss();
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', syncButtons); else syncButtons();
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', resumePosted); else setTimeout(resumePosted, 0);
  D.addEventListener('ij:navigated', function () { syncButtons(); });
  /* the cart page re-renders its rows after a quantity change / remove: label the new buttons too (not on builder pages,
     whose React tree mutates constantly - the bundle's controls track their own state) */
  if (W.MutationObserver && !D.getElementById('ij-charm-builder')) {
    var mqd = false;
    new W.MutationObserver(function () {
      if (mqd) return; mqd = true;
      W.requestAnimationFrame(function () { mqd = false; if (D.querySelector('[data-ij-save-design]:not([data-ij-sync])')) syncButtons(); });
    }).observe(D.documentElement, { childList: true, subtree: true });
  }

  W.IJSaved = {
    __v: 1,
    list: read,
    count: function () { return read().length; },
    find: find,
    save: save,
    rename: rename,
    setThumb: setThumb,
    remove: remove,
    restore: restore,
    onChange: onChange,
    open: openSheet,
    close: closeSheet,
    compact: compact,
    key: designKey,
    emailHref: emailHref,
    sync: syncButtons,
  };
  try { D.dispatchEvent(new CustomEvent('ij:saved-ready')); } catch (e) { /* old engines */ }
})();
